import { cn } from "../../lib/utils";
import { UserButton } from "@clerk/clerk-react";
import type { RouteId } from "../../lib/routes";
import { routes, isWorkBoardRoute } from "../../lib/routes";

interface TopbarProps {
  currentRoute: RouteId;
  onOpenCommandPalette?: () => void;
}

/** Tab labels for the breadcrumb when inside Work Board */
const WORK_TAB_LABELS: Partial<Record<RouteId, string>> = {
  board: "Kanban",
  path: "Runbook",
  timeline: "Timeline",
  pipeline: "Pipeline",
  checklist: "Checklist",
};

export function Topbar({ currentRoute, onOpenCommandPalette }: TopbarProps) {
  const routeConfig = routes.find((r) => r.id === currentRoute);
  const isWorkBoard = isWorkBoardRoute(currentRoute);
  const pageTitle = isWorkBoard ? "Work Board" : (routeConfig?.label ?? "Command Center");
  const tabLabel = isWorkBoard ? WORK_TAB_LABELS[currentRoute] : undefined;

  return (
    <header
      className={cn(
        "h-[var(--topbar-height)] shrink-0 flex items-center justify-between",
        "px-[var(--page-padding-desktop)]",
        "border-b border-[var(--color-border-stroke)] bg-[var(--color-bg-canvas)]",
      )}
    >
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-[var(--spacing-1_5)] min-w-0">
        <span className="text-[14px] font-medium text-[var(--color-text-muted)] shrink-0">
          AI Wave
        </span>
        <span className="text-[14px] text-[var(--color-text-muted)] shrink-0 select-none">/</span>
        <span className="text-[14px] text-[var(--color-text-muted)] shrink-0 select-none">…</span>
        <span className="text-[14px] text-[var(--color-text-muted)] shrink-0 select-none">/</span>
        <span className={cn("text-[14px] truncate", tabLabel ? "font-medium text-[var(--color-text-muted)]" : "font-semibold text-[var(--color-text-primary)]")}>
          {pageTitle}
        </span>
        {tabLabel && (
          <>
            <span className="text-[14px] text-[var(--color-text-muted)] shrink-0 select-none">/</span>
            <span className="text-[14px] font-semibold text-[var(--color-text-primary)] truncate">
              {tabLabel}
            </span>
          </>
        )}
      </nav>

      <div className="flex items-center gap-[var(--spacing-3)]">
        {/* ⌘K Search trigger */}
        <button
          onClick={onOpenCommandPalette}
          className={cn(
            "flex items-center gap-[var(--spacing-2)]",
            "rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-surface)] px-[var(--spacing-3)] py-[var(--spacing-1_5)]",
            "text-[13px] text-[var(--color-text-muted)]",
            "hover:bg-[var(--color-bg-hover)] hover:border-[var(--color-border-strong)]",
            "transition-colors duration-[120ms] cursor-pointer",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] focus-visible:ring-offset-2",
          )}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 opacity-60">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <span className="hidden sm:inline">Search…</span>
          <kbd className={cn(
            "inline-flex items-center gap-[2px]",
            "rounded-[4px] px-[5px] py-[1px]",
            "text-[11px] font-medium leading-none font-mono",
            "bg-[var(--color-bg-canvas)] text-[var(--color-text-muted)]",
            "border border-[var(--color-border-stroke)]",
            "select-none",
          )}>
            ⌘K
          </kbd>
        </button>

        <UserButton
          appearance={{
            elements: {
              avatarBox: "w-[28px] h-[28px]",
            },
          }}
        />
      </div>
    </header>
  );
}
